import React, { useEffect, useState } from "react";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ListImageName from "go-app/components/ImageRenderer/ListImageName";
import { ReactComponent as InfoCircleSVG } from "go-component/images/svg/info-circle.svg";
import { MoneyApi } from "go-core/api/types";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { useWindowSize } from "go-core/components/useWindowSize";
import { ReactComponent as DropdownSVG } from "go-core/images/svg/dropdown.svg";
import {
	ExternalProviderMenuItemApi,
	ExternalProviderMenuItemGroupApi,
	ExternalProviderMenuItemGroupOptionApi,
	ExternalProviderMenuItemGroupOptionGroupApi,
	ExternalProviderMenuItemGroupOptionGroupOptionApi,
	QuantityInfoApi,
} from "../../../../../../../../../../../../services/Api/Organization/types";

interface Props {
	rawItem:
		| ExternalProviderMenuItemApi
		| ExternalProviderMenuItemGroupApi
		| ExternalProviderMenuItemGroupOptionApi
		| ExternalProviderMenuItemGroupOptionGroupApi
		| ExternalProviderMenuItemGroupOptionGroupOptionApi;
	name: string;
	paddingLeft: number;
	quantityInfo?: QuantityInfoApi;
	imageUrl?: string;
	description?: string;
	quantity?: number;
	withImage?: boolean;
	price?: MoneyApi;
	showPriceWithPlus?: boolean;
	showExpandAllButton?: boolean;
	parentExpandAll?: boolean;
	level: number;
}

const MenuExternalPortalMenuItem = ({
	rawItem,
	description,
	price,
	quantityInfo,
	quantity,
	imageUrl,
	withImage,
	name,
	paddingLeft,
	showExpandAllButton,
	showPriceWithPlus,
	level,
	parentExpandAll,
}: Props) => {
	const { t } = useTranslation();
	const [expandItems, setExpandItems] = useState(parentExpandAll || false);
	const [expandAllItems, setExpandAllItems] = useState(parentExpandAll || false);
	const isMobile = useWindowSize().isMobile;

	useEffect(() => {
		setExpandItems(expandAllItems);
	}, [expandAllItems]);

	const getName = () => {
		if (quantity !== undefined) return `${name} (${quantity})`;
		return name;
	};

	const getItems = (
		raw:
			| ExternalProviderMenuItemApi
			| ExternalProviderMenuItemGroupApi
			| ExternalProviderMenuItemGroupOptionApi
			| ExternalProviderMenuItemGroupOptionGroupApi
			| ExternalProviderMenuItemGroupOptionGroupOptionApi
	) => {
		if ("options" in raw) {
			return raw.options;
		}
		if ("groups" in raw) {
			return raw.groups;
		}
		return [];
	};

	const getItemProps = (
		item:
			| ExternalProviderMenuItemApi
			| ExternalProviderMenuItemGroupApi
			| ExternalProviderMenuItemGroupOptionApi
			| ExternalProviderMenuItemGroupOptionGroupApi
			| ExternalProviderMenuItemGroupOptionGroupOptionApi
	): Props => {
		const isChildrenGroup = "groups" in rawItem;
		const isChildrenOption = "options" in rawItem;
		const childrenLevel = level + 1;
		const isFirstLevel = childrenLevel === 0;
		const isLastLevel = childrenLevel === 4;
		const showQuantityInfo = isChildrenGroup || isLastLevel;

		return {
			rawItem: item,
			name: item.name,
			quantityInfo: showQuantityInfo ? item.quantity_info : undefined,
			paddingLeft: paddingLeft + 18,
			level: childrenLevel,
			showExpandAllButton: childrenLevel > 0 && childrenLevel < 3,
			showPriceWithPlus: isChildrenOption,
			price: item.price,
			withImage: !isChildrenGroup,
			imageUrl: item.image_url,
			quantity: isChildrenGroup ? getItems(item).length : undefined,
			description: isLastLevel || isFirstLevel ? item.description : undefined,
			parentExpandAll: expandAllItems,
		};
	};

	const toggleExpandItems = () => {
		if (expandItems) {
			if (expandAllItems) {
				setExpandAllItems(false);
			} else {
				setExpandItems(false);
			}
		} else {
			setExpandItems(true);
		}
	};

	const onExpandAllPressed = () => {
		if (expandAllItems) {
			setExpandAllItems(false);
		} else if (expandItems) {
			setExpandItems(false);
		} else {
			setExpandAllItems(true);
		}
	};

	const getItemQuantityInfo = (info: QuantityInfoApi) => {
		const result: string[] = [];

		if (info.min_permitted !== null && info.min_permitted !== undefined) {
			result.push(`${t("modules.external_portal.field.minimum.title")}: ${info.min_permitted}`);
		}
		if (info.max_permitted !== null && info.max_permitted !== undefined) {
			result.push(`${t("modules.external_portal.field.maximum.title")}: ${info.max_permitted}`);
		}
		if (info.charge_above !== null && info.charge_above !== undefined) {
			result.push(`${t("modules.external_portal.field.charge_above.title")}: ${info.charge_above}`);
		}
		if (info.default_quantity !== null && info.default_quantity !== undefined) {
			result.push(`${t("modules.external_portal.field.default_quantity.title")}: ${info.default_quantity}`);
		}

		return result.join(" • ");
	};

	return (
		<>
			<tr className="sub-row">
				<td style={{ paddingLeft }}>
					<div
						className={`d-flex ${isMobile ? "align-items-start" : "align-items-center"} gap-2 ${
							getItems(rawItem).length > 0 ? "cursor-pointer" : ""
						}`}
						onClick={() => toggleExpandItems()}
					>
						{getItems(rawItem).length > 0 && (
							<DropdownSVG
								className={`dropdown-icon ${expandItems ? "--expanded" : ""}`}
								style={{ marginTop: isMobile ? "5px" : 0 }}
							/>
						)}
						{withImage && (
							<ListImageName data={{ name }} imageLink={imageUrl ? { small: imageUrl } : undefined} />
						)}
						<div>
							<div className="d-flex flex-column">
								<p className="mb-0 font-weight-bold">
									{getName()}
									{description && (
										<OverlayTrigger
											placement="top"
											overlay={
												<Tooltip id={`tooltip-${name}-${description}`}>{description}</Tooltip>
											}
										>
											<InfoCircleSVG className="ms-2" />
										</OverlayTrigger>
									)}
								</p>
								{quantityInfo && <small>{getItemQuantityInfo(quantityInfo)}</small>}
								{isMobile && (
									<div className="d-flex align-items-center gap-1">
										{showPriceWithPlus && "+"}
										{price && FormatMoney(price)}
									</div>
								)}
								{isMobile && (
									<div className="d-flex align-items-center gap-1">
										{showExpandAllButton && getItems(rawItem).length > 0 && (
											<span onClick={onExpandAllPressed} className="cursor-pointer text-primary">
												{expandItems && (
													<span>
														{t("modules.external_portal.action.collapse_all.title")}
													</span>
												)}
												{!expandItems && (
													<span>{t("modules.external_portal.action.expand_all.title")}</span>
												)}
												<DropdownSVG
													className={`dropdown-icon dropdown-icon-mobile ms-1 ${
														expandItems ? "--expanded" : ""
													}`}
												/>
											</span>
										)}
										{/*<span className="cursor-pointer text-primary">*/}
										{/*	{t("lib:common.action.edit")}*/}
										{/*</span>*/}
									</div>
								)}
							</div>
						</div>
					</div>
				</td>
				{!isMobile && (
					<>
						<td className="money-cell">
							<div className="d-flex align-items-center justify-content-end gap-2">
								{showPriceWithPlus && "+ "}
								{price && FormatMoney(price)}
								{showExpandAllButton && getItems(rawItem).length > 0 && (
									<span onClick={onExpandAllPressed} className="cursor-pointer text-primary">
										{expandItems && (
											<span>{t("modules.external_portal.action.collapse_all.title")}</span>
										)}
										{!expandItems && (
											<span>{t("modules.external_portal.action.expand_all.title")}</span>
										)}
										<DropdownSVG
											className={`dropdown-icon ms-1 ${expandItems ? "--expanded" : ""}`}
										/>
									</span>
								)}
							</div>
						</td>
						<td className="action">
							{/*<Button variant="add" className="mb-0">*/}
							{/*	{t("lib:common.action.edit")}*/}
							{/*</Button>*/}
						</td>
					</>
				)}
				{isMobile && <td />}
			</tr>
			{expandItems &&
				getItems(rawItem).map((item, index) => (
					<MenuExternalPortalMenuItem key={index} {...getItemProps(item)} />
				))}
		</>
	);
};

export default MenuExternalPortalMenuItem;
